import NumericInput from "@/components/NumericInput";
import type { Remediation } from "@/types";

/**
 * Configuring how to bring CYA down — and the numbers for deciding whether to.
 *
 * Options rather than one instruction. The app used to hardcode a twelve-inch
 * drain and state it as the remedy; twelve was a cautious guess made while
 * building the calculator, and the shallow end decides what is actually safe.
 *
 * Two things are deliberately not sold here. Reverse osmosis and CYA-reducing
 * products are shown greyed with their caveat, because whether an RO service
 * operates in a given county is not something a pool logbook can know, and the
 * products' results are inconsistent. And there is no projected chlorine
 * saving — see ADR 0011. What argues for acting is SLAM feasibility, which is
 * exact arithmetic and needs no prices.
 */
export default function CyaPlanSection({
  remediation,
  approach,
  stageInches,
  waterRate,
  startedOn,
  onApproach,
  onStageInches,
  onWaterRate,
  onStartedOn,
}: {
  remediation: Remediation | null;
  approach: string;
  stageInches: string;
  waterRate: string;
  startedOn: string;
  onApproach: (value: string) => void;
  onStageInches: (value: string) => void;
  onWaterRate: (value: string) => void;
  onStartedOn: (value: string) => void;
}) {
  if (!remediation) return null;

  const { slam_now: now, slam_at_goal: goal } = remediation;
  const ignored = approach === "ignored";

  return (
    <section className="card space-y-4 p-4">
      <div>
        <h2 className="font-medium">Cyanuric acid</h2>
        <p className="mt-1 text-sm text-(--color-ink-muted)">
          {remediation.current_cya
            ? `Currently ${remediation.current_cya} ppm, aiming for ${remediation.goal_cya}.`
            : "No CYA measurement on record."}
        </p>
      </div>

      {/* The argument for acting, before the controls for acting. It costs
          nothing to compute and does not depend on any price being entered. */}
      {now && goal && Number(now.gallons_to_reach) > Number(goal.gallons_to_reach) && (
        <div className="rounded-lg bg-(--color-surface-sunken) p-3">
          <p className="unit">If algae appeared</p>
          <p className="mt-1 text-sm">
            Clearing it means holding{" "}
            <strong className="figure">{now.slam_ppm}</strong> ppm free chlorine — about{" "}
            <strong className="figure">{now.gallons_to_reach}</strong> gal just to get there,
            then holding it for days. At {remediation.goal_cya} ppm CYA that would be{" "}
            <strong className="figure">{goal.gallons_to_reach}</strong> gal.
          </p>
          <p className="mt-1.5 text-xs text-(--color-ink-faint)">
            That is the cost of high CYA — not a bigger daily dose, but an algae bloom you
            cannot practically clear.
          </p>
        </div>
      )}

      <div>
        <label htmlFor="cya-approach" className="text-sm font-medium">
          Plan
        </label>
        <select
          id="cya-approach"
          value={approach}
          onChange={(e) => onApproach(e.target.value)}
          className="field mt-1.5 w-full"
        >
          <option value="">Not decided yet</option>
          {remediation.options.map((option) => (
            <option key={option.key} value={option.key} disabled={!option.available}>
              {option.label}
              {option.available ? "" : " — check availability first"}
            </option>
          ))}
        </select>
        {approach !== "" && (
          <p className="mt-1.5 text-xs text-(--color-ink-faint)">
            {remediation.options.find((o) => o.key === approach)?.caution}
          </p>
        )}
      </div>

      {ignored && (
        <p className="text-xs text-(--color-status-warn)">
          No drains will be suggested. Your chlorine requirement is unchanged — the floor and
          targets still reflect the CYA actually in the water.
        </p>
      )}

      {!ignored && (
        <div>
          <label htmlFor="cya-stage" className="text-sm font-medium">
            Water per drain
          </label>
          <div className="mt-1.5 flex items-center gap-2">
            <NumericInput
              id="cya-stage"
              value={stageInches}
              onChange={onStageInches}
              placeholder={String(remediation.stage_inches)}
              className="field figure w-24"
            />
            <span className="unit">inches</span>
          </div>
          <p className="mt-1.5 text-xs text-(--color-ink-faint)">{remediation.stage_reason}</p>
          {/* The consequence of the number, updated as it is typed. How many
              trips this becomes is the actual decision being made, and it is a
              poor one to answer by saving and re-reading. */}
          {remediation.plan && (
            <p className="mt-2 text-sm">
              <strong className="figure">{remediation.plan.stages.length}</strong>{" "}
              {remediation.plan.stages.length === 1 ? "drain" : "drains"} to reach{" "}
              {remediation.goal_cya} ppm —{" "}
              <span className="figure">
                {Number(remediation.plan.total_gallons).toLocaleString()}
              </span>{" "}
              gal in total
              {remediation.water_cost ? `, about $${remediation.water_cost} of water` : ""}.
            </p>
          )}

          <div className="mt-4">
            <label htmlFor="cya-started" className="text-sm font-medium">
              Plan started
            </label>
            <input
              id="cya-started"
              type="date"
              value={startedOn}
              onChange={(e) => onStartedOn(e.target.value)}
              className="field mt-1.5 w-full"
            />
            <p className="mt-1.5 text-xs text-(--color-ink-faint)">
              Back-date this to the drain you actually started with — drains logged before it
              are left out of the count. Blank counts every drain you have logged.
            </p>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="water-rate" className="text-sm font-medium">
          Water rate
        </label>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="unit">$</span>
          <NumericInput
            id="water-rate"
            value={waterRate}
            onChange={onWaterRate}
            className="field figure w-28"
            placeholder="—"
          />
          <span className="unit">per 1,000 gal</span>
        </div>
        <p className="mt-1.5 text-xs text-(--color-ink-faint)">
          Optional, and zero on a well. Only the water is costed — what draining saves in
          chlorine is not a figure anyone publishes honestly, so the app does not invent one.
        </p>
      </div>
    </section>
  );
}
